import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Calendar, Users, Sparkles, Mail, Download, Home, RefreshCw, DollarSign, Plane, User, Clock } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { MatchReasonsDisplay } from '../components/FeedbackComponents';
import { useLanguage } from '../contexts/LanguageContext';
import { translateMonth } from '../i18n/translations';
import '../styles/ResultsPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResultsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language, t, translateCountry, translateContinent } = useLanguage();
  
  // CRITICAL FIX: Initialize state from location.state OR localStorage fallback
  const [recommendations, setRecommendations] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [ctaSettings, setCtaSettings] = useState(null);
  const [newsletterOptIn, setNewsletterOptIn] = useState(false);

  // CRITICAL FIX: Load results from location.state OR localStorage
  useEffect(() => {
    console.log('[MOUNT] ResultsPage mounted');
    console.log('[MOUNT] location.state:', location.state);
    
    let resultsData = null;
    
    // Try to get data from location.state first
    if (location.state && location.state.recommendations && location.state.recommendations.length > 0) {
      console.log('[OK] Found results in location.state:', location.state.recommendations.length, 'destinations');
      resultsData = {
        recommendations: location.state.recommendations,
        answers: location.state.answers || []
      };
    } 
    // Fallback: Try localStorage
    else {
      console.log('[WARN] No results in location.state, checking localStorage...');
      try {
        const storedResults = localStorage.getItem('quiz_results');
        if (storedResults) {
          const parsed = JSON.parse(storedResults);
          console.log('[OK] Found results in localStorage:', parsed.recommendations?.length || 0, 'destinations');
          console.log('[TIME] Stored at:', parsed.timestamp);
          
          if (parsed.recommendations && parsed.recommendations.length > 0) {
            resultsData = {
              recommendations: parsed.recommendations,
              answers: parsed.answers || []
            };
          }
        } else {
          console.log('[ERROR] No results found in localStorage');
        }
      } catch (error) {
        console.error('[ERROR] Error parsing localStorage:', error);
      }
    }
    
    // Set the state if we found data
    if (resultsData) {
      setRecommendations(resultsData.recommendations);
      setAnswers(resultsData.answers);
      console.log('[OK] Results loaded successfully:', resultsData.recommendations.length, 'destinations');
    } else {
      console.log('[ERROR] No results found anywhere');
    }
    
    setIsLoading(false);
  }, [location.state]);

  // Load CTA settings
  useEffect(() => {
    const loadCTA = async () => {
      try {
        const res = await axios.get(`${API}/cta-settings`);
        setCtaSettings(res.data);
      } catch (e) { console.error('CTA load error:', e); }
    };
    loadCTA();
  }, []);

  // Helper to get answer by question ID
  const getAnswer = (questionId) => {
    if (!answers || answers.length === 0) return null;
    const answer = answers.find(a => a.question_id === questionId);
    return answer ? answer.answer : null;
  };

  // Helper to format budget values (from "25_40k" to "25.001€ - 40.000€")
  // Updated to support all 10 price tiers
  const formatBudgetValue = (budgetCode) => {
    const budgetMap = {
      de: {
        'bis_25k': 'Bis 25.000€',
        '20_40k': '20.000€ - 40.000€',
        '25_40k': '25.001€ - 40.000€',
        '40_60k': '40.001€ - 60.000€',
        '60_80k': '60.001€ - 80.000€',
        '60_90k': '60.001€ - 90.000€',
        '80_100k': '80.001€ - 100.000€',
        '100_120k': '100.001€ - 120.000€',
        '120_150k': '120.001€ - 150.000€',
        '150_200k': '150.001€ - 200.000€',
        '100k_plus': '100.000€+',
        '200k_plus': '200.000€+'
      },
      en: {
        'bis_25k': 'Up to €25,000',
        '20_40k': '€20,000 - €40,000',
        '25_40k': '€25,001 - €40,000',
        '40_60k': '€40,001 - €60,000',
        '60_80k': '€60,001 - €80,000',
        '60_90k': '€60,001 - €90,000',
        '80_100k': '€80,001 - €100,000',
        '100_120k': '€100,001 - €120,000',
        '120_150k': '€120,001 - €150,000',
        '150_200k': '€150,001 - €200,000',
        '100k_plus': '€100,000+',
        '200k_plus': '€200,000+'
      },
      es: {
        'bis_25k': 'Hasta €25,000',
        '20_40k': '€20,000 - €40,000',
        '25_40k': '€25,001 - €40,000',
        '40_60k': '€40,001 - €60,000',
        '60_80k': '€60,001 - €80,000',
        '60_90k': '€60,001 - €90,000',
        '80_100k': '€80,001 - €100,000',
        '100_120k': '€100,001 - €120,000',
        '120_150k': '€120,001 - €150,000',
        '150_200k': '€150,001 - €200,000',
        '100k_plus': '€100,000+',
        '200k_plus': '€200,000+'
      }
    };
    return budgetMap[language]?.[budgetCode] || budgetMap['de'][budgetCode] || budgetCode;
  };

  // Helper to translate values
  const translateValue = (key, value) => {
    const translations = {
      de: {
        climate: {
          'tropisch & warm': 'Tropisch & Warm',
          'mediterran & mild': 'Mediterran & Mild',
          'gemäßigt & kühl': 'Gemäßigt & Kühl',
          'trocken & sonnig': 'Trocken & Sonnig',
          'alpin & polar': 'Alpin & Polar'
        },
        scenarios: {
          'Strand': 'Strand',
          'Regenwald': 'Regenwald',
          'Berge': 'Berge',
          'City': 'City',
          'Wüste': 'Wüste'
        },
        cultural: {
          'Yoga & Spiritualität': 'Yoga & Spiritualität',
          'Tanz & Musik': 'Tanz & Musik',
          'Kulinarik & Wein': 'Kulinarik & Wein',
          'Kunst & Design': 'Kunst & Design'
        },
        ambiance: {
          'Rustikal': 'Rustikal',
          'Modern': 'Modern',
          'Exotisch': 'Exotisch',
          'Royal': 'Royal'
        },
        distance: {
          '1': 'Nah (Europa)',
          '2': 'Mittel (Nordafrika, Naher Osten)',
          '3': 'Fern (Asien, Afrika)',
          '4': 'Weit (Amerika, Ozeanien)'
        },
        guests: {
          'zu_zweit': 'Zu zweit',
          'ca_15': 'Ca. 15 Gäste',
          'ca_25': 'Ca. 25 Gäste',
          'ca_40': 'Ca. 40 Gäste',
          'mehr_40': 'Mehr als 40 Gäste'
        },
        duration: {
          '1': '1 Tag',
          '2': '2 Tage',
          '3': '3 Tage',
          '4': '4 Tage'
        },
        budget: {
          'bis_25k': 'Bis 25.000€',
          '20_40k': '20.000€ - 40.000€',
          '25_40k': '25.001€ - 40.000€',
          '40_60k': '40.001€ - 60.000€',
          '60_90k': '60.001€ - 90.000€',
          '100k_plus': '100.000€+'
        }
      },
      en: {
        climate: {
          'tropisch & warm': 'Tropical & Warm',
          'mediterran & mild': 'Mediterranean & Mild',
          'gemäßigt & kühl': 'Temperate & Cool',
          'trocken & sonnig': 'Dry & Sunny',
          'alpin & polar': 'Alpine & Polar'
        },
        scenarios: {
          'Strand': 'Beach',
          'Regenwald': 'Rainforest',
          'Berge': 'Mountains',
          'City': 'City',
          'Wüste': 'Desert'
        },
        cultural: {
          'Yoga & Spiritualität': 'Yoga & Spirituality',
          'Tanz & Musik': 'Dance & Music',
          'Kulinarik & Wein': 'Culinary & Wine',
          'Kunst & Design': 'Art & Design'
        },
        ambiance: {
          'Rustikal': 'Rustic',
          'Modern': 'Modern',
          'Exotisch': 'Exotic',
          'Royal': 'Royal'
        },
        distance: {
          '1': 'Near (Europe)',
          '2': 'Medium (North Africa, Middle East)',
          '3': 'Far (Asia, Africa)',
          '4': 'Very Far (America, Oceania)'
        },
        guests: {
          'zu_zweit': 'Just two',
          'ca_15': 'About 15 guests',
          'ca_25': 'About 25 guests',
          'ca_40': 'About 40 guests',
          'mehr_40': 'More than 40 guests'
        },
        duration: {
          '1': '1 Day',
          '2': '2 Days',
          '3': '3 Days',
          '4': '4 Days'
        },
        budget: {
          'bis_25k': 'Up to €25,000',
          '20_40k': '€20,000 - €40,000',
          '25_40k': '€25,001 - €40,000',
          '40_60k': '€40,001 - €60,000',
          '60_90k': '€60,001 - €90,000',
          '100k_plus': '€100,000+'
        }
      },
      es: {
        climate: {
          'tropisch & warm': 'Tropical y Cálido',
          'mediterran & mild': 'Mediterráneo y Suave',
          'gemäßigt & kühl': 'Templado y Fresco',
          'trocken & sonnig': 'Seco y Soleado',
          'alpin & polar': 'Alpino y Polar'
        },
        scenarios: {
          'Strand': 'Playa',
          'Regenwald': 'Selva',
          'Berge': 'Montañas',
          'City': 'Ciudad',
          'Wüste': 'Desierto'
        },
        cultural: {
          'Yoga & Spiritualität': 'Yoga y Espiritualidad',
          'Tanz & Musik': 'Danza y Música',
          'Kulinarik & Wein': 'Gastronomía y Vino',
          'Kunst & Design': 'Arte y Diseño'
        },
        ambiance: {
          'Rustikal': 'Rústico',
          'Modern': 'Moderno',
          'Exotisch': 'Exótico',
          'Royal': 'Real'
        },
        distance: {
          '1': 'Cerca (Europa)',
          '2': 'Medio (Norte de África, Oriente Medio)',
          '3': 'Lejos (Asia, África)',
          '4': 'Muy Lejos (América, Oceanía)'
        },
        guests: {
          'zu_zweit': 'Solo dos',
          'ca_15': 'Aprox. 15 invitados',
          'ca_25': 'Aprox. 25 invitados',
          'ca_40': 'Aprox. 40 invitados',
          'mehr_40': 'Más de 40 invitados'
        },
        duration: {
          '1': '1 Día',
          '2': '2 Días',
          '3': '3 Días',
          '4': '4 Días'
        },
        budget: {
          'bis_25k': 'Hasta €25,000',
          '20_40k': '€20,000 - €40,000',
          '25_40k': '€25,001 - €40,000',
          '40_60k': '€40,001 - €60,000',
          '60_90k': '€60,001 - €90,000',
          '100k_plus': '€100,000+'
        }
      }
    };
    
    return translations[language]?.[key]?.[value] || translations['de']?.[key]?.[value] || value;
  };

  // Helper to get translated label
  const getLabel = (labelKey) => {
    const labels = {
      de: {
        travelTime: 'Reisezeit',
        travelDistance: 'Reisedistanz',
        climateZone: 'Klimazone',
        locationType: 'Location-Typ',
        groupSize: 'Gruppengröße',
        duration: 'Dauer',
        budgetRange: 'Budgetrahmen',
        bestTravelTime: 'Beste Reisezeit',
        estimatedBudget: 'Geschätztes Budget',
        yourPreferences: 'Ihre Präferenzen:',
        whyThisMatch: 'Warum dieser Match?'
      },
      en: {
        travelTime: 'Travel Time',
        travelDistance: 'Travel Distance',
        climateZone: 'Climate Zone',
        locationType: 'Location Type',
        groupSize: 'Group Size',
        duration: 'Duration',
        budgetRange: 'Budget Range',
        bestTravelTime: 'Best Travel Time',
        estimatedBudget: 'Estimated Budget',
        yourPreferences: 'Your Preferences:',
        whyThisMatch: 'Why This Match?'
      },
      es: {
        travelTime: 'Época de Viaje',
        travelDistance: 'Distancia de Viaje',
        climateZone: 'Zona Climática',
        locationType: 'Tipo de Ubicación',
        groupSize: 'Tamaño del Grupo',
        duration: 'Duración',
        budgetRange: 'Rango de Presupuesto',
        bestTravelTime: 'Mejor Época de Viaje',
        estimatedBudget: 'Presupuesto Estimado',
        yourPreferences: 'Tus Preferencias:',
        whyThisMatch: '¿Por Qué Este Match?'
      }
    };
    
    return labels[language]?.[labelKey] || labels['de'][labelKey];
  };

  // Helper to translate season values
  const translateSeason = (season) => {
    return translateMonth(season, language);
  };

  // Generate match explanation with user parameters and context
  const getMatchFactors = (destination) => {
    const factors = [];
    
    // Season match
    const season = getAnswer('q1');
    if (season && destination.best_season?.includes(season)) {
      factors.push({
        icon: <Calendar size={16} />,
        label: getLabel('travelTime'),
        value: translateMonth(season, language),
        context: t('results.seasonMatches') || 'Ihre gewünschte Hochzeitszeit passt perfekt zu dieser Destination',
        matched: true
      });
    }

    // Distance match
    const distance = getAnswer('q2');
    if (distance && destination.distance === parseInt(distance)) {
      factors.push({
        icon: <Plane size={16} />,
        label: getLabel('travelDistance'),
        value: translateValue('distance', distance),
        context: t('results.distanceMatches') || 'Die Entfernung entspricht Ihren Vorstellungen',
        matched: true
      });
    }

    // Climate match
    const climate = getAnswer('q4');
    if (climate && destination.climate === climate) {
      factors.push({
        icon: <Sparkles size={16} />,
        label: getLabel('climateZone'),
        value: translateValue('climate', climate),
        context: t('results.climateMatches') || 'Das Klima ist genau wie von Ihnen gewünscht',
        matched: true
      });
    }

    // Scenario match
    const scenario = getAnswer('q5');
    if (scenario && destination.scenarios?.includes(scenario)) {
      factors.push({
        icon: <MapPin size={16} />,
        label: getLabel('locationType'),
        value: translateValue('scenarios', scenario),
        context: t('results.scenarioMatches') || 'Die Umgebung passt zu Ihrer Traumhochzeit',
        matched: true
      });
    }

    // Guest count - ALWAYS show with checkmark
    const guests = getAnswer('q6');
    if (guests) {
      factors.push({
        icon: <Users size={16} />,
        label: getLabel('groupSize'),
        value: translateValue('guests', guests),
        context: t('results.guestCount') || 'Geeignet für Ihre geplante Gästezahl',
        matched: true  // Always show as matched per user request
      });
    }
    
    // Duration (number of days) - ALWAYS show with checkmark
    const days = getAnswer('q7');
    if (days) {
      factors.push({
        icon: <Clock size={16} />,
        label: getLabel('duration'),
        value: translateValue('duration', days),
        context: language === 'de' ? 'Passende Dauer für Ihre Feier' : language === 'en' ? 'Suitable duration for your celebration' : 'Duración adecuada para su celebración',
        matched: true
      });
    }

    // Budget
    const budget = getAnswer('q8');
    if (budget) {
      factors.push({
        icon: <DollarSign size={16} />,
        label: getLabel('budgetRange'),
        value: formatBudgetValue(budget),  // Use formatted budget
        context: t('results.budgetRange') || 'Liegt in Ihrem gewünschten Budgetbereich',
        matched: destination.required_budget ? true : false
      });
    }

    return factors;
  };

  // Get destination image - use database image_url if available
  const getDestinationImage = (destination) => {
    // Use image_url from database if available
    if (destination.image_url) {
      return destination.image_url;
    }
    
    // Fallback to placeholder based on continent
    const continentColors = {
      'Europa': 'cb9ac5',      // Violett (Corporate Design)
      'Afrika': 'c9c8e4',      // Lila
      'Asien': 'e1dad0',       // Beige
      'Amerika': 'e73a2c',     // Rot
      'Ozeanien': 'cb9ac5'     // Violett
    };
    
    const color = continentColors[destination.continent] || 'e1dad0';
    const text = encodeURIComponent(destination.name);
    
    // Using via.placeholder.com which is CORS-friendly
    return `https://via.placeholder.com/800x600/${color}/FFFFFF?text=${text}`;
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="results-container" data-testid="results-page">
        <div className="loading-container" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: '20px'
        }}>
          <div className="spinner" style={{
            width: '48px',
            height: '48px',
            border: '4px solid #e5e7eb',
            borderTop: '4px solid #8b5cf6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ color: '#6b7280' }}>
            {language === 'de' ? 'Ergebnisse werden geladen...' 
             : language === 'en' ? 'Loading results...' 
             : 'Cargando resultados...'}
          </p>
        </div>
      </div>
    );
  }

  // Show error state if no results
  if (!recommendations || recommendations.length === 0) {
    return (
      <div className="results-container" data-testid="results-page">
        <div className="error-container">
          <h2>
            {language === 'de' ? 'Keine Ergebnisse gefunden' 
             : language === 'en' ? 'No results found' 
             : 'No se encontraron resultados'}
          </h2>
          <p>
            {language === 'de' ? 'Bitte führen Sie zuerst das Quiz durch.' 
             : language === 'en' ? 'Please complete the quiz first.' 
             : 'Por favor, complete el cuestionario primero.'}
          </p>
          <button className="btn-primary" onClick={() => navigate('/quiz')}>
            {language === 'de' ? 'Zum Quiz' 
             : language === 'en' ? 'Go to Quiz' 
             : 'Ir al cuestionario'}
          </button>
        </div>
      </div>
    );
  }

  const handleSaveAndExport = async () => {
    if (!email || !email.includes('@')) {
      toast.error(t('results.errorEmail') || 'Bitte gib eine gültige E-Mail-Adresse ein');
      return;
    }

    if (!name || name.trim().length < 2) {
      toast.error(t('results.errorName') || 'Bitte gib deinen Namen ein');
      return;
    }

    if (!city || city.trim().length < 2) {
      toast.error(t('results.errorCity') || 'Bitte gib deinen Ort ein');
      return;
    }

    setIsSaving(true);
    try {
      await axios.post(`${API}/results`, {
        email,
        name,
        city,
        answers,
        recommended_destinations: recommendations.map(r => r.name),
        language: language // Send language to backend
      });

      toast.success(t('results.successMessage') || 'Deine Ergebnisse wurden per E-Mail versendet!');
      setShowEmailModal(false);
      setEmail('');
      setName('');
      setCity('');

      // Newsletter subscription
      if (newsletterOptIn) {
        try {
          await axios.post(`${API}/newsletter/subscribe`, {
            email, name, source: 'quiz_results'
          });
        } catch (e) { console.error('Newsletter error:', e); }
        setNewsletterOptIn(false);
      }
    } catch (error) {
      console.error('Error saving results:', error);
      toast.error(t('results.errorMessage') || 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.');
    } finally {
      setIsSaving(false);
    }
  };

  // Show all recommendations (not just top 3)
  const allRecommendations = recommendations;
  const numResults = allRecommendations.length;

  return (
    <div className="results-container" data-testid="results-page">
      {/* Header with Navigation */}
      <div className="results-header">
        <div className="header-logo">
          <a href="https://www.animoo.de" target="_blank" rel="noopener noreferrer">
            <img src="/animoo-logo.png" alt="animoo weddings" className="results-logo-image" />
          </a>
        </div>
        
        <div className="header-navigation">
          <button 
            className="nav-button"
            onClick={() => navigate('/')}
            title={t('results.navHome')}
          >
            <Home size={20} />
            <span>{t('results.navHome')}</span>
          </button>
          <button 
            className="nav-button"
            onClick={() => navigate('/quiz', { 
              state: { 
                resumeAnswers: answers,
                goToLastQuestion: true 
              } 
            })}
            title={t('results.navLastQuestion')}
          >
            <RefreshCw size={20} />
            <span>{t('results.navLastQuestion')}</span>
          </button>
        </div>
        
        <div className="header-content">
          <h1 className="results-title" data-testid="results-title">
            {t('results.title')}
          </h1>
          <p className="results-subtitle">
            {t('results.subtitle', { count: numResults })}
          </p>
        </div>
      </div>

      {/* CTA Button */}
      {ctaSettings?.enabled && (
        <div className="cta-banner" data-testid="cta-banner">
          <a
            href={ctaSettings.button_url || 'https://www.animooweddings.com/kontakt'}
            target="_blank"
            rel="noopener noreferrer"
            className="cta-banner-btn"
            data-testid="cta-banner-btn"
          >
            <Sparkles size={20} />
            {language === 'de' ? ctaSettings.button_text_de 
             : language === 'en' ? ctaSettings.button_text_en 
             : language === 'es' ? ctaSettings.button_text_es 
             : ctaSettings.button_text_de}
          </a>
        </div>
      )}

      {/* All Destinations */}
      <div className="destinations-container">
        {allRecommendations.map((destination, index) => {
          const matchFactors = getMatchFactors(destination);
          const matchPercentage = Math.round((destination.score / 165) * 100);

          return (
            <div
              key={index}
              className={`destination-card rank-${index + 1}`}
              data-testid={`destination-${index + 1}`}
            >
              <div className="rank-badge">{index + 1}</div>
              
              <div className="destination-image-container">
                <img
                  src={getDestinationImage(destination)}
                  alt={destination.name}
                  className="destination-image"
                  onError={(e) => {
                    // Fallback to solid color if placeholder fails
                    e.target.style.background = 'linear-gradient(135deg, #D4AF37 0%, #B8941F 100%)';
                    e.target.style.display = 'flex';
                    e.target.style.alignItems = 'center';
                    e.target.style.justifyContent = 'center';
                    e.target.alt = destination.name;
                  }}
                />
                <div className="destination-overlay">
                  <span className="match-score">{matchPercentage}% Match</span>
                </div>
              </div>

              <div className="destination-content">
                <div className="destination-header">
                  <h2 className="destination-name">{translateCountry(destination.name, language)}</h2>
                  <p className="destination-continent">
                    <MapPin size={16} />
                    {translateContinent(destination.continent, language)}
                  </p>
                </div>

                {/* Match Factors - Visual Parameter Display */}
                <div className="match-factors">
                  <h3 className="factors-title">
                    <Sparkles size={16} />
                    {getLabel('whyThisMatch')}
                  </h3>
                  
                  {/* SCHRITT 2: Match Reasons Display */}
                  {destination.match_reasons && destination.match_reasons.length > 0 && (
                    <MatchReasonsDisplay reasons={destination.match_reasons} />
                  )}
                  
                  <div className="factors-grid">
                    {matchFactors.map((factor, i) => (
                      <div 
                        key={i} 
                        className={`factor-item ${factor.matched ? 'matched' : ''}`}
                      >
                        <div className="factor-icon">{factor.icon}</div>
                        <div className="factor-content">
                          <span className="factor-label">{factor.label}</span>
                          <span className="factor-value">{factor.value}</span>
                        </div>
                        {factor.matched && (
                          <div className="factor-check">✓</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Destination Details */}
                <div className="destination-details">
                  <div className="detail-item">
                    <Calendar size={18} className="detail-icon" />
                    <div>
                      <span className="detail-label">{t('results.bestSeason')}</span>
                      <span className="detail-value">
                        {destination.best_season?.slice(0, 3).map(s => translateSeason(s)).join(', ')}
                      </span>
                    </div>
                  </div>

                  {destination.required_budget && (
                    <div className="detail-item">
                      <DollarSign size={18} className="detail-icon" />
                      <div>
                        <span className="detail-label">{getLabel('estimatedBudget')}</span>
                        <span className="detail-value">
                          ca. {destination.required_budget.toLocaleString('de-DE')}€
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                </div>
            </div>
          );
        })}
      </div>

      {/* Action Buttons */}
      <div className="actions-container">
        <button
          className="btn-secondary"
          onClick={() => navigate('/quiz')}
          data-testid="restart-quiz-button"
        >
          <RefreshCw size={20} />
          {t('results.footerRetakeQuiz')}
        </button>

        <button
          className="btn-primary"
          onClick={() => setShowEmailModal(true)}
          data-testid="save-results-button"
        >
          <Mail size={20} />
          {t('results.footerSaveResults')}
        </button>

        <button
          className="btn-secondary"
          onClick={() => navigate('/')}
          data-testid="home-button"
        >
          <Home size={20} />
          {t('results.footerBackHome')}
        </button>
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <div className="modal-overlay" onClick={() => setShowEmailModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">{t('results.modalTitle')}</h3>
            <p className="modal-description">
              {t('results.modalDesc')}
            </p>

            <div className="modal-form">
              <div className="form-row">
                <div className="form-field">
                  <Label htmlFor="name">{t('results.name')}</Label>
                  <div className="input-with-icon">
                    <User size={18} />
                    <Input
                      id="name"
                      type="text"
                      placeholder={t('results.namePlaceholder')}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="email-input"
                    />
                  </div>
                </div>

                <div className="form-field">
                  <Label htmlFor="city">{t('results.city')}</Label>
                  <div className="input-with-icon">
                    <MapPin size={18} />
                    <Input
                      id="city"
                      type="text"
                      placeholder={t('results.cityPlaceholder')}
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      className="email-input"
                    />
                  </div>
                </div>
              </div>

              <div className="form-field">
                <Label htmlFor="email">{t('results.email')}</Label>
                <div className="input-with-icon">
                  <Mail size={18} />
                  <Input
                    id="email"
                    type="email"
                    placeholder={t('results.emailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="email-input"
                    data-testid="email-input"
                  />
                </div>
              </div>
            </div>

            {/* Newsletter Opt-in */}
            <label className="newsletter-optin" data-testid="newsletter-checkbox">
              <input
                type="checkbox"
                checked={newsletterOptIn}
                onChange={(e) => setNewsletterOptIn(e.target.checked)}
              />
              <span className="newsletter-label">
                {language === 'de' ? (ctaSettings?.newsletter_text_de || 'Ich möchte über neue Destinationen & Angebote informiert werden')
                  : language === 'en' ? (ctaSettings?.newsletter_text_en || 'I want to be informed about new destinations & offers')
                  : (ctaSettings?.newsletter_text_es || 'Quiero recibir información sobre nuevos destinos y ofertas')}
              </span>
            </label>

            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setShowEmailModal(false)}
                disabled={isSaving}
              >
                {t('results.cancel')}
              </button>
              <button
                className="btn-primary"
                onClick={handleSaveAndExport}
                disabled={isSaving}
                data-testid="confirm-save-button"
              >
                {isSaving ? (
                  <>
                    <Download size={20} className="animate-spin" />
                    {t('results.sending')}
                  </>
                ) : (
                  <>
                    <Mail size={20} />
                    {t('results.send')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* CSS for spinner animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ResultsPage;
